"use client";

import React, { createContext, useContext, useState, useCallback, ReactNode } from "react";

export interface FollowUpTaskMessageData {
  subject: string;
  body: string;
  recipients: string[];
  ccRecipients?: string[];
  bccRecipients?: string[];
  fromAccount: string;
  fromAccountLabel?: string;
  selectedClient?: {
    id: string;
    name: string;
    email: string;
  };
}

interface FollowUpTaskContextType {
  messageData: FollowUpTaskMessageData | null;
  setMessageData: (data: FollowUpTaskMessageData | null) => void;
  onTaskCreated?: (task: any) => void;
  setOnTaskCreated: (callback: ((task: any) => void) | undefined) => void;
}

const FollowUpTaskContext = createContext<FollowUpTaskContextType | undefined>(undefined);

interface FollowUpTaskProviderProps {
  children: ReactNode;
}

export function FollowUpTaskProvider({ children }: FollowUpTaskProviderProps) {
  const [messageData, setMessageData] = useState<FollowUpTaskMessageData | null>(null);
  const [onTaskCreated, setOnTaskCreated] = useState<((task: any) => void) | undefined>(undefined);

  // Debug logging
  React.useEffect(() => {
    if (process.env.NODE_ENV === "development") {
      console.log("🔍 FollowUpTaskContext messageData updated:", messageData);
    }
  }, [messageData]);

  const contextValue: FollowUpTaskContextType = {
    messageData,
    setMessageData,
    onTaskCreated,
    setOnTaskCreated,
  };

  return (
    <FollowUpTaskContext.Provider value={contextValue}>{children}</FollowUpTaskContext.Provider>
  );
}

export function useFollowUpTaskContext() {
  const context = useContext(FollowUpTaskContext);
  if (context === undefined) {
    throw new Error("useFollowUpTaskContext must be used within a FollowUpTaskProvider");
  }
  return context;
}
